import {
  Settings,
  AccountCircle,
  ArrowForwardOutlined,
  ExpandMore,
  Favorite
} from '@mui/icons-material';
import {
  Button,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC, useState, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { isPlaceOrderAdmin, APP_VERSION } from '../../helpers';
import { IUser } from '../../models';
import { NotificationBadge } from '../../pages/news/notification-badge';
import { useQuery, useQueryClient } from 'react-query';
import { getVersion, deimpersonateEntraUser } from '../../fetch';
import { UserContext } from '../../context';
import { useAllowedNavRoutes, useFlags } from '../../hooks';

interface IUserMenu {
  showHelp: boolean;
  user: IUser;
  isMobile: boolean;
  showNews?: boolean;
  /* top level page prop to hide the menu option when on the no-roles page */
  showSettings?: boolean;
}

export const UserMenu: FC<IUserMenu> = ({ user, showNews = true, showSettings = true }) => {
  const featureFlags = useFlags();

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { loadUser } = useContext(UserContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const LSVersion = localStorage.getItem(APP_VERSION);
  const [isDeimpersonating, setDeimpersonating] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { availableLinks } = useAllowedNavRoutes(featureFlags, user);

  const { data: version } = useQuery<any, Error>(['getVersion'], () => getVersion(), {
    enabled: !LSVersion
  });

  const displayName = () => {
    if (user?.impersonatedByFullName) {
      return 'Impersonating - ' + (user?.fullName ?? user?.userName);
    } else {
      return user?.fullName ?? user?.userName;
    }
  };

  const isShowingFavoriteOrders = useMemo(() => {
    return featureFlags.authorizeUserNavigationAccess
      ? availableLinks.has('Favorite Orders')
      : isPlaceOrderAdmin(user);
  }, [featureFlags, availableLinks, user]);

  return (
    <>
      <Grid item className={classes.userWrapper}>
        <div className={classes.content}>
          {showNews && (
            <div className={classes.badge}>
              <NotificationBadge />
            </div>
          )}
          <Button
            onClick={event => setAnchorEl(event.currentTarget)}
            size="medium"
            aria-controls="customized-menu"
            aria-haspopup="true"
            color="primary"
            variant="contained"
            startIcon={<AccountCircle />}
            endIcon={<ExpandMore />}
          >
            <Typography
              component="span"
              sx={{
                display: {
                  xs: 'none',
                  sm: 'inline-block'
                }
              }}
            >
              {displayName()}
            </Typography>
          </Button>
          <Menu
            id="customized-menu"
            anchorEl={anchorEl}
            keepMounted
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            {showSettings && (
              <CustomerMenuItem
                icon={<Settings fontSize="small" />}
                pathname="/user-settings"
                text="Settings"
              />
            )}
            {user?.impersonatedByFullName ? (
              <MenuItem
                onClick={async () => {
                  setAnchorEl(null);
                  try {
                    setDeimpersonating(true);
                    await deimpersonateEntraUser();
                    loadUser();
                    // Invalidate the getAvailableNavigation query to refresh the navigation
                    // Have to do this to ignore the cache after impersonating
                    queryClient.invalidateQueries(['getAvailableNavigation']);
                    enqueueSnackbar('Stopped impersonating successfully. Redirecting...', {
                      variant: 'success'
                    });
                    history.push('/');
                    setDeimpersonating(false);
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ?? 'Error stopping impersonation.', {
                      variant: 'error'
                    });
                    setDeimpersonating(false);
                  }
                }}
                className={clsx(classes.menuItem)}
                disabled={isDeimpersonating}
              >
                <ListItemIcon>
                  <ArrowForwardOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Stop Impersonating</ListItemText>
              </MenuItem>
            ) : (
              <CustomerMenuItem
                icon={<ArrowForwardOutlined fontSize="small" />}
                pathname="/logout"
                text="Logout"
              />
            )}
            {isShowingFavoriteOrders && (
              <CustomerMenuItem
                icon={<Favorite fontSize="small" />}
                pathname="/favorite-orders"
                text="Favorite Orders"
              />
            )}
            <Typography className={classes.version}>
              Version {LSVersion || version?.build}
            </Typography>
          </Menu>
        </div>
      </Grid>
    </>
  );
};

const CustomerMenuItem = ({ icon, pathname, text }) => {
  const history = useHistory();
  const classes = useStyles();
  return (
    <MenuItem onClick={() => history.push(pathname)} className={clsx(classes.menuItem)}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{text}</ListItemText>
    </MenuItem>
  );
};

const useStyles = makeStyles<Theme>((theme: Theme) => {
  return {
    content: {
      display: 'flex',
      height: '100%',
      alignItems: 'center',
      flex: '1',
      justifyContent: 'flex-end'
    },
    userWrapper: {
      flexGrow: 1
    },
    menuItem: {
      color: theme.palette.secondary.main
    },
    badge: {
      marginRight: theme.spacing(1.25)
    },
    version: {
      color: theme.palette.grey[600],
      fontSize: '0.75rem',
      marginTop: '4px',
      marginLeft: '19px'
    }
  };
});
